import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatMXN, imageUrl, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  CheckCircle, CaretLeft, CaretRight, X, UploadSimple, MapPin, House, Bed,
  Bathtub, Ruler, Star, FloppyDisk, Eye,
} from "@phosphor-icons/react";
import { PropertyCard } from "../components/PropertyCard";
import { PublicationAuditor } from "../components/PublicationAuditor";
import { AtlasDraftModal } from "../components/AtlasDraftModal";
import { PublishSuccessShare } from "../components/PublishSuccessShare";
import { Sparkle } from "@phosphor-icons/react";
import imageCompression from "browser-image-compression";

// Compresión en cliente antes del upload — acelera cargas de 20+ fotos.
// - Ignora archivos no-imagen (video/otros) o ya pequeños (<300 KB).
// - Reduce a máx 2560 px (largo mayor) y objetivo ~1.2 MB por foto.
// - Usa Web Worker para no bloquear la UI.
// - Si la compresión falla, se sube el archivo original.
const COMPRESS_OPTS = {
  maxSizeMB: 1.2,
  maxWidthOrHeight: 2560,
  useWebWorker: true,
  initialQuality: 0.82,
  fileType: "image/jpeg",
};
const COMPRESS_THRESHOLD_BYTES = 300 * 1024;

const maybeCompress = async (file) => {
  if (!file?.type?.startsWith("image/")) return file;
  if (file.size <= COMPRESS_THRESHOLD_BYTES) return file;
  try {
    const compressed = await imageCompression(file, COMPRESS_OPTS);
    // Si la "compresión" resulta más grande, quédate con el original.
    return compressed.size < file.size ? compressed : file;
  } catch {
    return file;
  }
};

const AUTOSAVE_KEY = "rf_property_draft_v1";
const AUTOSAVE_DEBOUNCE_MS = 800;

const STEPS = [
  { key: "basics",   label: "Básicos",       hint: "Título, tipo y precio" },
  { key: "details",  label: "Detalles",      hint: "Recámaras y superficie" },
  { key: "location", label: "Ubicación",     hint: "Dirección y mapa" },
  { key: "media",    label: "Fotografías",   hint: "Al menos 1 imagen" },
  { key: "features", label: "Características", hint: "Amenidades (opcional)" },
  { key: "preview",  label: "Vista previa",  hint: "Confirmar y publicar" },
];

const INITIAL_FORM = {
  title: "",
  description: "",
  price: "",
  property_type: "casa",
  operation: "venta",
  bedrooms: 2,
  bathrooms: 1,
  area_m2: 0,
  address: "",
  city: "",
  state: "",
  latitude: "",
  longitude: "",
  images: [],
  features: [],
  status: "activa",
};

// --- Step validators. Return null if valid, or string with error. ---
const validators = {
  basics: (f) => {
    if (!f.title?.trim() || f.title.trim().length < 4) return "Escribe un título de al menos 4 caracteres.";
    if (!f.description?.trim() || f.description.trim().length < 20) return "Añade una descripción de al menos 20 caracteres.";
    if (!f.property_type) return "Selecciona el tipo de propiedad.";
    if (!f.operation) return "Selecciona la operación.";
    const p = Number(f.price);
    if (!p || p <= 0) return "Ingresa un precio válido (mayor a 0).";
    return null;
  },
  details: (f) => {
    if (Number(f.bedrooms) < 0) return "Recámaras no puede ser negativo.";
    if (Number(f.bathrooms) < 0) return "Baños no puede ser negativo.";
    if (Number(f.area_m2) <= 0) return "Ingresa la superficie en m².";
    return null;
  },
  location: (f) => {
    if (!f.address?.trim()) return "Ingresa la dirección.";
    if (!f.city?.trim()) return "Ingresa la ciudad.";
    if (!f.state?.trim()) return "Ingresa el estado.";
    return null;
  },
  media: (f) => {
    if (!f.images || f.images.length === 0) return "Agrega al menos 1 fotografía.";
    return null;
  },
  features: () => null,
  preview: () => null,
};

export function PropertyWizard({ mode = "create" }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState(INITIAL_FORM);
  const [stepIndex, setStepIndex] = useState(0);
  const [stepError, setStepError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [autosavedAt, setAutosavedAt] = useState(null);
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  const [featureInput, setFeatureInput] = useState("");
  const initialLoadDoneRef = useRef(false);
  // Fase 5.3 — ATLAS content helpers
  const [atlasKind, setAtlasKind] = useState(null); // 'title' | 'description' | null
  const [publishedProperty, setPublishedProperty] = useState(null);

  const step = STEPS[stepIndex];
  const isEdit = mode === "edit";

  // --- Load: edit mode fetches from API. Create mode checks autosave. ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (isEdit && id) {
        try {
          const { data } = await api.get(`/properties/${id}`);
          if (cancelled) return;
          setForm({
            ...INITIAL_FORM,
            ...data,
            price: String(data.price ?? ""),
            latitude: data.latitude ?? "",
            longitude: data.longitude ?? "",
            images: data.images ?? [],
            features: data.features ?? [],
            status: data.status ?? "activa",
          });
        } catch (err) {
          toast.error(formatApiError(err, "No pudimos cargar la propiedad"));
        }
      } else {
        try {
          const raw = localStorage.getItem(AUTOSAVE_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.form && parsed?.savedAt) {
              setShowResumeBanner(true);
            }
          }
        } catch (_e) { /* ignore */ }
      }
      initialLoadDoneRef.current = true;
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  // --- Autosave (debounced) for create mode only. ---
  useEffect(() => {
    if (isEdit) return;
    if (!initialLoadDoneRef.current) return;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(AUTOSAVE_KEY, JSON.stringify({ form, stepIndex, savedAt: Date.now() }));
        setAutosavedAt(new Date());
      } catch (_e) { /* quota errors ignored */ }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [form, stepIndex, isEdit]);

  const resumeDraft = () => {
    try {
      const raw = localStorage.getItem(AUTOSAVE_KEY);
      const parsed = JSON.parse(raw || "{}");
      if (parsed.form) {
        setForm({ ...INITIAL_FORM, ...parsed.form });
        setStepIndex(Math.min(parsed.stepIndex ?? 0, STEPS.length - 1));
      }
    } finally { setShowResumeBanner(false); }
  };
  const discardDraft = () => {
    localStorage.removeItem(AUTOSAVE_KEY);
    setShowResumeBanner(false);
  };

  const setField = (patch) => setForm(f => ({ ...f, ...patch }));

  // --- Navigation with per-step validation. ---
  const goNext = () => {
    const err = validators[step.key]?.(form);
    if (err) { setStepError(err); return; }
    setStepError("");
    setStepIndex(i => Math.min(i + 1, STEPS.length - 1));
  };
  const goPrev = () => { setStepError(""); setStepIndex(i => Math.max(i - 1, 0)); };
  const gotoStep = (i) => {
    // Allow going back freely; forward jumps require validation of all preceding steps.
    if (i <= stepIndex) { setStepError(""); setStepIndex(i); return; }
    for (let k = stepIndex; k < i; k++) {
      const err = validators[STEPS[k].key]?.(form);
      if (err) { setStepIndex(k); setStepError(err); return; }
    }
    setStepError("");
    setStepIndex(i);
  };

  // --- Image upload ---
  // Uploads en paralelo con concurrencia limitada (soporta 20+ fotos).
  // Cada archivo se sube por separado; si uno falla, los demás continúan.
  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const total = files.length;
    const results = new Array(total);
    let done = 0, failed = 0;
    const CONCURRENCY = 3;
    let idx = 0;

    const worker = async () => {
      while (idx < total) {
        const i = idx++;
        const rawFile = files[i];
        const file = await maybeCompress(rawFile);
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await api.post("/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60_000,
          });
          results[i] = data.url;
        } catch (err) {
          failed++;
          results[i] = null;
        } finally {
          done++;
          if (total > 5) toast.message(`Subiendo ${done}/${total}...`, { id: "upload-progress" });
        }
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, total) }, worker));
      const urls = results.filter(Boolean);
      if (urls.length) setForm(f => ({ ...f, images: [...f.images, ...urls] }));
      if (failed === 0)      toast.success(`${urls.length} imagen(es) cargada(s)`);
      else if (urls.length)  toast.success(`${urls.length} de ${total} imagen(es) cargada(s). ${failed} falló(fallaron), reintenta esas.`);
      else                   toast.error("No se pudieron subir las imágenes. Reintenta.");
    } finally { setUploading(false); }
  };
  const removeImage = (i) => setForm(f => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  const moveImage = (i, dir) => setForm(f => {
    const arr = [...f.images];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, images: arr };
  });

  const addFeature = () => {
    const t = featureInput.trim();
    if (!t) return;
    if (form.features.includes(t)) { setFeatureInput(""); return; }
    setForm(f => ({ ...f, features: [...f.features, t] }));
    setFeatureInput("");
  };
  const removeFeature = (i) => setForm(f => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));

  // --- Submit ---
  // For active publications we validate all steps. For drafts we only require
  // a minimal title so the agent can save partial work at any moment.
  const submit = async (statusOverride) => {
    const isDraft = statusOverride === "borrador";
    if (isDraft) {
      if (!form.title?.trim() || form.title.trim().length < 4) {
        setStepIndex(0);
        setStepError("Añade al menos un título para poder guardar el borrador.");
        toast.error("El título es obligatorio para guardar un borrador.");
        return;
      }
    } else {
      for (const s of STEPS) {
        if (s.key === "preview") continue;
        const err = validators[s.key]?.(form);
        if (err) {
          setStepIndex(STEPS.findIndex(x => x.key === s.key));
          setStepError(err);
          toast.error("Revisa los datos antes de publicar.");
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        status: statusOverride || form.status || "activa",
        price: Number(form.price) || 0,
        bedrooms: Number(form.bedrooms) || 0,
        bathrooms: Number(form.bathrooms) || 0,
        area_m2: Number(form.area_m2) || 0,
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
        // Drafts allow empty required strings; backend accepts them because the
        // schema only enforces string type, not non-emptiness.
        description: form.description || (isDraft ? "" : form.description),
        address: form.address || (isDraft ? "" : form.address),
        city: form.city || (isDraft ? "" : form.city),
        state: form.state || (isDraft ? "" : form.state),
      };
      if (isEdit) {
        await api.put(`/properties/${id}`, payload);
        toast.success("Propiedad actualizada");
        navigate("/dashboard");
      } else {
        const { data: created } = await api.post("/properties", payload);
        toast.success(isDraft ? "Guardado como borrador" : "¡Propiedad publicada!");
        localStorage.removeItem(AUTOSAVE_KEY);
        if (!isDraft && created?.id) {
          // Fase 5.3 — mostrar opción destacada "Compartir propiedad".
          setPublishedProperty(created);
        } else {
          navigate("/dashboard");
        }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  const progress = Math.round(((stepIndex + 1) / STEPS.length) * 100);
  const previewProperty = useMemo(() => ({
    id: id || "preview",
    ...form,
    price: Number(form.price) || 0,
    bedrooms: Number(form.bedrooms) || 0,
    bathrooms: Number(form.bathrooms) || 0,
    area_m2: Number(form.area_m2) || 0,
  }), [form, id]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="wizard-page">
      <Link to="/dashboard" className="text-sm text-[#C8A24A]/70 hover:text-[#C8A24A]" data-testid="wizard-back-link">← Volver al panel</Link>
      <div className="flex items-start justify-between mt-4 mb-6 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold">Publicación</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-2 tracking-tight" data-testid="wizard-title">
            {isEdit ? "Editar propiedad" : "Nueva propiedad"}
          </h1>
        </div>
        {!isEdit && autosavedAt && (
          <div className="flex items-center gap-2 text-xs text-[#C8A24A]/70" data-testid="autosave-indicator">
            <FloppyDisk size={14} weight="duotone" />
            Autoguardado {autosavedAt.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
          </div>
        )}
      </div>

      {/* Resume draft banner */}
      {showResumeBanner && (
        <div className="mb-6 rounded-2xl border border-[#C8A24A]/40 bg-[#C8A24A]/10 p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="resume-banner">
          <div>
            <p className="text-sm font-semibold text-[#C8A24A]">Tienes un borrador sin publicar</p>
            <p className="text-xs text-[#C8A24A]/70 mt-1">Podemos retomar donde te quedaste.</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={discardDraft} data-testid="resume-discard-btn">Descartar</Button>
            <Button size="sm" className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111]" onClick={resumeDraft} data-testid="resume-continue-btn">
              Continuar borrador
            </Button>
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2">
          <span data-testid="wizard-step-label">Paso {stepIndex + 1} de {STEPS.length} · {step.label}</span>
          <span data-testid="wizard-progress-pct">{progress}%</span>
        </div>
        <div className="h-1.5 w-full bg-[#1A1A1A] rounded-full overflow-hidden border border-[#C8A24A]/10">
          <div
            className="h-full bg-gradient-to-r from-[#C8A24A] to-[#B88C2D] transition-[width] duration-500"
            style={{ width: `${progress}%` }}
            data-testid="wizard-progress-bar"
          />
        </div>
      </div>

      {/* Stepper (desktop) */}
      <div className="hidden md:flex items-center gap-2 mb-8 overflow-x-auto pb-2" data-testid="wizard-stepper">
        {STEPS.map((s, i) => {
          const done = i < stepIndex;
          const active = i === stepIndex;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => gotoStep(i)}
              data-testid={`stepper-${s.key}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs transition-colors whitespace-nowrap ${
                active ? "border-[#C8A24A] bg-[#C8A24A]/10 text-[#C8A24A]"
                : done ? "border-[#C8A24A]/30 text-[#C8A24A]/80 hover:bg-[#C8A24A]/5"
                : "border-[#C8A24A]/10 text-[#C8A24A]/50 hover:border-[#C8A24A]/30"
              }`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                done ? "bg-[#C8A24A] text-black" : active ? "bg-[#C8A24A] text-black" : "bg-[#1A1A1A] border border-[#C8A24A]/20"
              }`}>
                {done ? <CheckCircle size={12} weight="fill" /> : i + 1}
              </span>
              <span className="font-semibold">{s.label}</span>
              <span className="hidden lg:inline text-xs opacity-70">· {s.hint}</span>
            </button>
          );
        })}
      </div>

      {/* Step content */}
      <div className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-5 sm:p-8" data-testid={`step-${step.key}`}>
        {step.key === "basics"   && <StepBasics form={form} setField={setField} onAtlas={setAtlasKind} />}
        {step.key === "details"  && <StepDetails form={form} setField={setField} />}
        {step.key === "location" && <StepLocation form={form} setField={setField} />}
        {step.key === "media"    && <StepMedia form={form} uploading={uploading} onUpload={uploadFiles} onRemove={removeImage} onMove={moveImage} />}
        {step.key === "features" && <StepFeatures form={form} featureInput={featureInput} setFeatureInput={setFeatureInput} addFeature={addFeature} removeFeature={removeFeature} />}
        {step.key === "preview"  && <StepPreview property={previewProperty} form={form} setField={setField} />}
      </div>

      {stepError && (
        <p className="mt-3 text-sm text-destructive" data-testid="wizard-step-error">{stepError}</p>
      )}

      {/* Navigation */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={goPrev}
          disabled={stepIndex === 0}
          data-testid="wizard-prev-btn"
          className="h-11"
        >
          <CaretLeft size={16} className="mr-1" /> Anterior
        </Button>

        {step.key !== "preview" ? (
          <Button
            type="button"
            onClick={goNext}
            data-testid="wizard-next-btn"
            className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-11 px-6"
          >
            Siguiente <CaretRight size={16} className="ml-1" />
          </Button>
        ) : (
          <Button
            type="button"
            onClick={() => submit("activa")}
            disabled={submitting}
            data-testid="wizard-publish-btn"
            className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-11 px-6"
          >
            {submitting ? "Publicando..." : (isEdit ? "Guardar cambios" : "Publicar propiedad")}
          </Button>
        )}

        {!isEdit && (
          <Button
            type="button"
            variant="outline"
            onClick={() => submit("borrador")}
            disabled={submitting}
            data-testid="wizard-save-draft-btn"
            className="h-11 ml-auto"
          >
            <FloppyDisk size={14} className="mr-1" /> Guardar como borrador
          </Button>
        )}
      </div>

      {/* ATLAS Content Generator — Fase 5.3 */}
      {atlasKind && (
        <AtlasDraftModal
          open={!!atlasKind}
          kind={atlasKind}
          propertyData={{
            title: form.title,
            description: form.description,
            price: Number(form.price) || 0,
            property_type: form.property_type,
            operation: form.operation,
            bedrooms: Number(form.bedrooms) || 0,
            bathrooms: Number(form.bathrooms) || 0,
            area_m2: Number(form.area_m2) || 0,
            address: form.address,
            city: form.city,
            state: form.state,
            features: form.features || [],
          }}
          onApply={(text) => {
            if (atlasKind === "title") setField({ title: text });
            else if (atlasKind === "description") setField({ description: text });
          }}
          onClose={() => setAtlasKind(null)}
        />
      )}

      {/* Publish Success + Share — Fase 5.3 */}
      {publishedProperty && (
        <PublishSuccessShare
          property={publishedProperty}
          onClose={() => setPublishedProperty(null)}
        />
      )}
    </div>
  );
}

// =================== Steps ===================

function StepBasics({ form, setField, onAtlas }) {
  return (
    <div className="grid gap-5">
      <SectionTitle title="Información básica" hint="Empieza con lo esencial que verá el comprador." />
      <FieldRow
        label="Título"
        htmlFor="wf-title"
        action={
          <button
            type="button"
            onClick={() => onAtlas?.("title")}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#C8A24A] hover:text-[#E5C275] transition-colors"
            data-testid="wf-title-atlas-btn"
          >
            <Sparkle size={11} weight="fill" /> Crear título con ATLAS
          </button>
        }
      >
        <Input id="wf-title" value={form.title} onChange={(e) => setField({ title: e.target.value })} placeholder="Ej. Casa moderna con alberca en San Pedro" data-testid="wf-title" />
      </FieldRow>
      <FieldRow
        label="Descripción"
        htmlFor="wf-description"
        action={
          <button
            type="button"
            onClick={() => onAtlas?.("description")}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#C8A24A] hover:text-[#E5C275] transition-colors"
            data-testid="wf-description-atlas-btn"
          >
            <Sparkle size={11} weight="fill" /> Crear descripción con ATLAS
          </button>
        }
      >
        <Textarea id="wf-description" rows={5} value={form.description} onChange={(e) => setField({ description: e.target.value })} placeholder="Describe la propiedad con detalle: acabados, entorno, plusvalía..." data-testid="wf-description" />
      </FieldRow>
      <div className="grid sm:grid-cols-2 gap-4">
        <FieldRow label="Operación">
          <Select value={form.operation} onValueChange={(v) => setField({ operation: v })}>
            <SelectTrigger data-testid="wf-operation"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="venta">Venta</SelectItem>
              <SelectItem value="renta">Renta</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Tipo de propiedad">
          <Select value={form.property_type} onValueChange={(v) => setField({ property_type: v })}>
            <SelectTrigger data-testid="wf-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="casa">Casa</SelectItem>
              <SelectItem value="departamento">Departamento</SelectItem>
              <SelectItem value="terreno">Terreno</SelectItem>
              <SelectItem value="oficina">Oficina</SelectItem>
              <SelectItem value="local">Local</SelectItem>
              <SelectItem value="bodega">Bodega</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>
      </div>
      <FieldRow label="Precio (MXN)" htmlFor="wf-price">
        <Input id="wf-price" type="number" min="0" value={form.price} onChange={(e) => setField({ price: e.target.value })} placeholder="0" data-testid="wf-price" />
      </FieldRow>
    </div>
  );
}

function StepDetails({ form, setField }) {
  return (
    <div className="grid gap-5">
      <SectionTitle title="Detalles de la propiedad" hint="Métricas clave que filtrará el comprador." />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FieldRow label="Recámaras" icon={Bed}>
          <Input type="number" min="0" value={form.bedrooms} onChange={(e) => setField({ bedrooms: e.target.value })} data-testid="wf-bedrooms" />
        </FieldRow>
        <FieldRow label="Baños" icon={Bathtub}>
          <Input type="number" step="0.5" min="0" value={form.bathrooms} onChange={(e) => setField({ bathrooms: e.target.value })} data-testid="wf-bathrooms" />
        </FieldRow>
        <FieldRow label="Superficie (m²)" icon={Ruler}>
          <Input type="number" min="0" value={form.area_m2} onChange={(e) => setField({ area_m2: e.target.value })} data-testid="wf-area" />
        </FieldRow>
      </div>
    </div>
  );
}

function StepLocation({ form, setField }) {
  return (
    <div className="grid gap-5">
      <SectionTitle title="Ubicación" hint="Añade coordenadas si quieres mostrar el mapa en el detalle." />
      <FieldRow label="Dirección">
        <Input value={form.address} onChange={(e) => setField({ address: e.target.value })} placeholder="Calle y número" data-testid="wf-address" />
      </FieldRow>
      <div className="grid sm:grid-cols-2 gap-4">
        <FieldRow label="Ciudad">
          <Input value={form.city} onChange={(e) => setField({ city: e.target.value })} data-testid="wf-city" />
        </FieldRow>
        <FieldRow label="Estado">
          <Input value={form.state} onChange={(e) => setField({ state: e.target.value })} data-testid="wf-state" />
        </FieldRow>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <FieldRow label="Latitud (opcional)">
          <Input type="number" step="any" value={form.latitude} onChange={(e) => setField({ latitude: e.target.value })} placeholder="19.4326" data-testid="wf-lat" />
        </FieldRow>
        <FieldRow label="Longitud (opcional)">
          <Input type="number" step="any" value={form.longitude} onChange={(e) => setField({ longitude: e.target.value })} placeholder="-99.1332" data-testid="wf-lng" />
        </FieldRow>
      </div>
    </div>
  );
}

function StepMedia({ form, uploading, onUpload, onRemove, onMove }) {
  return (
    <div className="grid gap-5">
      <SectionTitle title="Fotografías" hint="La primera imagen es la portada." />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {form.images.map((img, i) => (
          <div key={img + i} className="relative aspect-square rounded-xl overflow-hidden bg-[#0A0A0A] group border border-[#C8A24A]/10" data-testid={`wf-image-${i}`}>
            <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
            {i === 0 && (
              <span className="absolute top-2 left-2 text-xs uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#C8A24A] text-[#111111]">
                Portada
              </span>
            )}
            <div className="absolute inset-x-1 bottom-1 flex justify-between opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="flex gap-1">
                <button type="button" onClick={() => onMove(i, -1)} disabled={i === 0} className="w-7 h-7 rounded-full bg-black/70 text-white text-xs disabled:opacity-30" data-testid={`wf-move-left-${i}`}>◄</button>
                <button type="button" onClick={() => onMove(i, +1)} disabled={i === form.images.length - 1} className="w-7 h-7 rounded-full bg-black/70 text-white text-xs disabled:opacity-30" data-testid={`wf-move-right-${i}`}>►</button>
              </div>
              <button type="button" onClick={() => onRemove(i)} className="w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center" data-testid={`wf-remove-${i}`}>
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
        <label className="aspect-square rounded-xl border-2 border-dashed border-[#C8A24A]/30 hover:border-[#C8A24A] cursor-pointer flex flex-col items-center justify-center text-[#C8A24A]/70 hover:text-[#C8A24A] transition-colors" data-testid="wf-upload-label">
          <UploadSimple size={24} />
          <span className="text-xs mt-1 text-center px-2">{uploading ? "Subiendo..." : "Agregar fotos"}</span>
          <input type="file" accept="image/*" multiple className="hidden" disabled={uploading}
            onChange={(e) => e.target.files.length && onUpload([...e.target.files])} data-testid="wf-upload-input" />
        </label>
      </div>
      <p className="text-xs text-[#C8A24A]/60">Recomendamos 6-10 fotografías en horizontal para el mejor resultado. Puedes subir hasta 40; se cargan en paralelo.</p>
    </div>
  );
}

function StepFeatures({ form, featureInput, setFeatureInput, addFeature, removeFeature }) {
  const suggestions = ["Alberca", "Estacionamiento", "Jardín", "Terraza", "Seguridad 24/7", "Gimnasio", "Roof garden", "Amueblado", "Vista"];
  return (
    <div className="grid gap-5">
      <SectionTitle title="Características" hint="Amenidades y ventajas comerciales (opcional pero recomendado)." />
      <div className="flex gap-2">
        <Input
          value={featureInput}
          onChange={(e) => setFeatureInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
          placeholder="Escribe una característica y presiona Enter"
          data-testid="wf-feature-input"
        />
        <Button type="button" variant="outline" onClick={addFeature} data-testid="wf-feature-add-btn">Añadir</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {suggestions.filter(s => !form.features.includes(s)).map(s => (
          <button
            key={s}
            type="button"
            onClick={() => { setFeatureInput(s); setTimeout(addFeature, 0); }}
            className="px-3 py-1.5 rounded-full border border-[#C8A24A]/30 text-xs text-[#C8A24A]/80 hover:bg-[#C8A24A]/10"
            data-testid={`wf-feature-suggest-${s.toLowerCase().replace(/\s+/g,'-')}`}
          >
            + {s}
          </button>
        ))}
      </div>
      {form.features.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2">Agregadas</p>
          <div className="flex flex-wrap gap-2" data-testid="wf-features-list">
            {form.features.map((f, i) => (
              <span key={f + i} className="px-3 py-1 bg-[#0A0A0A] border border-[#C8A24A]/30 rounded-full text-sm inline-flex items-center gap-2">
                <Star size={12} weight="fill" className="text-[#C8A24A]" />
                {f}
                <button type="button" onClick={() => removeFeature(i)} data-testid={`wf-feature-remove-${i}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StepPreview({ property, form, setField }) {
  return (
    <div className="grid gap-6">
      <SectionTitle title="Vista previa" hint="Así verá el comprador tu propiedad." icon={Eye} />
      <PublicationAuditor
        propertyData={form}
        onApplyField={(field, value) => setField({ [field]: value })}
      />
      <div className="grid md:grid-cols-2 gap-6">
        {/* Card preview (list view) */}
        <div>
          <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2">Tarjeta en el listado</p>
          <div data-testid="preview-card">
            <PropertyCard property={property} />
          </div>
        </div>
        {/* Detail preview */}
        <div>
          <p className="text-xs uppercase tracking-widest text-[#C8A24A]/60 font-semibold mb-2">Página de detalle</p>
          <DetailPreview property={property} />
        </div>
      </div>
      <div className="rounded-2xl border border-[#C8A24A]/30 bg-[#C8A24A]/5 p-4 flex items-start gap-3" data-testid="preview-confirm-notice">
        <CheckCircle size={20} weight="fill" className="text-[#C8A24A] mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-[#C8A24A] font-semibold">Todo listo para publicar</p>
          <p className="text-xs text-[#C8A24A]/70 mt-1">Al confirmar, la propiedad quedará visible en el listado público para todos los compradores.</p>
        </div>
      </div>
    </div>
  );
}

function DetailPreview({ property }) {
  const cover = property.images?.[0];
  return (
    <div className="rounded-3xl overflow-hidden bg-[#0A0A0A] border border-[#C8A24A]/20" data-testid="preview-detail">
      <div className="relative aspect-[16/10] bg-[#1A1A1A]">
        {cover ? (
          <img src={imageUrl(cover)} alt={property.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#C8A24A]/30">
            <House size={40} />
          </div>
        )}
      </div>
      <div className="p-5">
        <p className="text-xs uppercase tracking-widest text-[#C8A24A]/70">
          {property.operation === "renta" ? "En renta" : "En venta"} · {property.property_type}
        </p>
        <h3 className="font-heading text-2xl mt-1 tracking-tight">{property.title || "Título de la propiedad"}</h3>
        <p className="text-sm text-[#C8A24A]/70 flex items-center gap-1 mt-1">
          <MapPin size={14} weight="fill" className="text-[#C8A24A]" />
          {[property.address, property.city, property.state].filter(Boolean).join(", ") || "Ubicación"}
        </p>
        <p className="text-3xl font-heading font-bold text-[#C8A24A] mt-3">{formatMXN(property.price || 0)}</p>
        <div className="flex gap-4 mt-4 text-sm text-[#C8A24A]/80 border-y border-[#C8A24A]/10 py-3">
          <span className="flex items-center gap-1"><Bed size={16} /> {property.bedrooms} rec.</span>
          <span className="flex items-center gap-1"><Bathtub size={16} /> {property.bathrooms} baños</span>
          <span className="flex items-center gap-1"><Ruler size={16} /> {property.area_m2} m²</span>
        </div>
        <p className="text-sm text-[#C8A24A]/80 mt-4 line-clamp-4 whitespace-pre-wrap">
          {property.description || "Descripción de la propiedad..."}
        </p>
        {property.features?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {property.features.slice(0, 6).map((f, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded-full border border-[#C8A24A]/20 text-[#C8A24A]/80">
                {f}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Small UI helpers ---
function SectionTitle({ title, hint, icon: Icon }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h2 className="font-heading text-2xl tracking-tight flex items-center gap-2">
          {Icon && <Icon size={20} weight="duotone" className="text-[#C8A24A]" />}
          {title}
        </h2>
        {hint && <p className="text-sm text-[#C8A24A]/60 mt-1">{hint}</p>}
      </div>
    </div>
  );
}

function FieldRow({ label, htmlFor, icon: Icon, action, children }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label htmlFor={htmlFor} className="text-xs uppercase tracking-[0.15em] font-semibold text-[#C8A24A]/80 flex items-center gap-1.5">
          {Icon && <Icon size={12} weight="duotone" />}
          {label}
        </Label>
        {action}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
